/**
 * Knowledge asset kind provider.
 *
 * Exported from @skaile/library for explicit registration by the CLI
 * at bootstrap. Discovery does NOT auto-include this provider
 * (discovery must stay a leaf — no library imports).
 *
 * Implements IAssetKindProvider with knowledge-specific:
 * - Discovery patterns: knowledge.yaml, knowledge/index.yaml
 * - Manifest validation via KnowledgeManifestSchema
 * - File filter: manifest + documents/ subdirectory
 * - No requires extraction (knowledge has no cross-asset deps)
 */

import * as fs from "node:fs";
import * as path from "node:path";
import type { AssetKindFileFilter, IAssetKindProvider } from "@skaile/workspaces/plugins";
import { validateKnowledge } from "./knowledge-manifest.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function collectDirRecursive(baseDir: string, relativeDir: string): string[] {
  const full = path.join(baseDir, relativeDir);
  if (!fs.existsSync(full) || !fs.statSync(full).isDirectory()) return [];
  const results: string[] = [];
  for (const entry of fs.readdirSync(full, { withFileTypes: true })) {
    const rel = path.posix.join(relativeDir, entry.name);
    if (entry.isDirectory()) {
      results.push(...collectDirRecursive(baseDir, rel));
    } else if (entry.isFile()) {
      results.push(rel);
    }
  }
  return results;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const knowledgeKindProvider: IAssetKindProvider = {
  kind: "knowledge",
  displayName: "Knowledge",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/knowledge.yaml", "**/knowledge/index.yaml"],

  matchesPath(relativePath: string): boolean {
    const parts = relativePath.split("/");
    const filename = parts[parts.length - 1] ?? "";
    if (filename === "knowledge.yaml") return true;
    if (filename === "index.yaml" && parts.length >= 2 && parts[parts.length - 2] === "knowledge") {
      return true;
    }
    return false;
  },

  validateManifest(data: unknown) {
    return validateKnowledge(data);
  },

  defaultFileFilter(manifestPath: string): AssetKindFileFilter {
    const dir = path.dirname(manifestPath);
    const manifestName = path.basename(manifestPath);
    const files = [manifestName];
    files.push(...collectDirRecursive(dir, "documents"));
    files.sort();
    return { assetRoot: dir, files };
  },

  // Knowledge has no cross-asset dependency edges — omit extractRequires
};
